import React from "react";
import { motion } from "framer-motion";
import { Calendar, Mail, Shield, AlertTriangle, Crown, Gavel, Users, Settings } from "lucide-react";

const Terms = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const stagger = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white pt-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial="initial"
          animate="animate"
          variants={stagger}
        >
          {/* Header */}
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                <Shield className="w-8 h-8 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Terms of Service
            </h1>
            <div className="flex justify-center items-center gap-4 text-gray-400">
              <Calendar className="w-4 h-4" />
              <span>Last updated: June 4, 2025</span>
            </div>
            <p className="text-gray-300 mt-4 max-w-2xl mx-auto">
              By using Voxel ("the Bot") on Discord, you agree to the following terms:
            </p>
          </motion.div>

          {/* Content */}
          <motion.div variants={stagger} className="prose prose-invert max-w-none">
            <motion.section variants={fadeInUp} className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <Users className="w-6 h-6 text-blue-400" />
                1. Usage Terms
              </h2>
              <div className="bg-slate-900/50 rounded-lg p-6 border border-slate-800">
                <ul className="list-disc list-inside text-gray-300 space-y-3">
                  <li>You must comply with Discord's Terms of Service and Community Guidelines.</li>
                  <li>You may not use the Bot for abusive, illegal, or harmful purposes.</li>
                  <li>We reserve the right to restrict or ban users or servers for abuse or violations.</li>
                  <li>You are responsible for ensuring your server's compliance with these terms when using Voxel.</li>
                </ul>
              </div>
            </motion.section>

            <motion.section variants={fadeInUp} className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <Settings className="w-6 h-6 text-green-400" />
                2. Feature Availability
              </h2>
              <div className="bg-slate-900/50 rounded-lg p-6 border border-slate-800">
                <ul className="list-disc list-inside text-gray-300 space-y-3">
                  <li>Features provided by the Bot are subject to change, removal, or replacement at any time.</li>
                  <li>Some features may be limited to premium users.</li>
                  <li>We strive to provide advance notice of major feature changes when possible.</li>
                  <li>Downtime for maintenance or updates may occur with minimal notice.</li>
                </ul>
              </div>
            </motion.section>

            <motion.section variants={fadeInUp} className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <Crown className="w-6 h-6 text-yellow-400" />
                3. Premium Features
              </h2>
              <div className="bg-slate-900/50 rounded-lg p-6 border border-slate-800">
                <ul className="list-disc list-inside text-gray-300 space-y-3">
                  <li>Premium access may be granted via Patreon or other support channels.</li>
                  <li>Abuse of premium access (e.g., bypassing limits) will result in removal of access.</li>
                  <li>Premium benefits are non-transferable and linked to your Discord account.</li>
                  <li>We reserve the right to modify premium features and benefits at any time.</li>
                </ul>
              </div>
            </motion.section>

            <motion.section variants={fadeInUp} className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <Shield className="w-6 h-6 text-purple-400" />
                4. Virtual Economy & Gaming
              </h2>
              <div className="bg-slate-900/50 rounded-lg p-6 border border-slate-800">
                <ul className="list-disc list-inside text-gray-300 space-y-3">
                  <li>Virtual currency and items within the Bot have no real-world monetary value.</li>
                  <li>Virtual currency cannot be exchanged for real money or transferred outside the Bot.</li>
                  <li>We may reset, modify, or remove virtual currency balances for maintenance or anti-abuse measures.</li>
                  <li>Exploitation of economy features or gambling systems may result in account restrictions.</li>
                  <li>The Bot's gambling features are for entertainment only and simulate casino games.</li>
                </ul>
              </div>
            </motion.section>

            <motion.section variants={fadeInUp} className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-red-400" />
                5. Limitation of Liability
              </h2>
              <div className="bg-slate-900/50 rounded-lg p-6 border border-slate-800">
                <ul className="list-disc list-inside text-gray-300 space-y-3">
                  <li>The Bot is provided "as is" with no warranty of any kind.</li>
                  <li>We are not liable for any damages, data loss, or issues arising from use of the Bot.</li>
                  <li>Users assume all risks associated with Bot usage, including virtual economy participation.</li>
                  <li>We are not responsible for Discord service interruptions affecting Bot functionality.</li>
                </ul>
              </div>
            </motion.section>

            <motion.section variants={fadeInUp} className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <Gavel className="w-6 h-6 text-orange-400" />
                6. Intellectual Property
              </h2>
              <div className="bg-slate-900/50 rounded-lg p-6 border border-slate-800">
                <ul className="list-disc list-inside text-gray-300 space-y-3">
                  <li>Voxel and its features are proprietary software owned by the development team.</li>
                  <li>Users may not reverse engineer, modify, or redistribute the Bot or its components.</li>
                  <li>All Bot-generated content remains the property of the Voxel development team.</li>
                  <li>The Voxel name, logo, and branding are protected intellectual property.</li>
                </ul>
              </div>
            </motion.section>

            <motion.section variants={fadeInUp} className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">7. Termination</h2>
              <div className="bg-slate-900/50 rounded-lg p-6 border border-slate-800">
                <p className="text-gray-300 leading-relaxed mb-4">
                  We reserve the right to terminate or suspend access to the Bot for any reason, including violations of these Terms. 
                  Termination may be temporary or permanent, and may affect individual users or entire servers.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  Users may discontinue Bot usage at any time by removing it from their Discord server or ceasing to interact with it.
                </p>
              </div>
            </motion.section>

            <motion.section variants={fadeInUp} className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">8. Modifications</h2>
              <div className="bg-slate-900/50 rounded-lg p-6 border border-slate-800">
                <p className="text-gray-300 leading-relaxed mb-4">
                  We may update these Terms at any time. Continued use of the Bot constitutes acceptance of the updated terms.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  Significant changes will be announced in our support server when possible.
                </p>
              </div>
            </motion.section>

            <motion.section variants={fadeInUp} className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">9. Governing Law</h2>
              <div className="bg-slate-900/50 rounded-lg p-6 border border-slate-800">
                <p className="text-gray-300 leading-relaxed">
                  These Terms are governed by applicable laws. Any disputes will be resolved through appropriate legal channels. 
                  By using Voxel, you agree to these terms and Discord's Terms of Service.
                </p>
              </div>
            </motion.section>

            <motion.section variants={fadeInUp} className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">10. Contact</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                For questions, issues, or concerns:
              </p>
              <div className="bg-slate-900 rounded-lg p-6 flex items-center gap-4">
                <Mail className="w-6 h-6 text-blue-400" />
                <div>
                  <a 
                    href="https://discord.gg/CfnpERCY7r" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 transition-colors font-medium"
                  >
                    Join our Support Server
                  </a>
                  <p className="text-gray-400 text-sm mt-1">We typically respond within 24 hours</p>
                </div>
              </div>
            </motion.section>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default Terms;
